import { icon, node } from "~/util/dom";
import { gpxLog } from "~/gpx/util";
import Dialog from "~/ui/dialog";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { standardDistanceFormat, standardTimeFormat } from "~/util/format";
import type GPXContent from "./content";
import ActivitySelect from "~/ui/activities/select";
import type SceneCoords from "~/scene/common/coords";
import type SceneMarkerWindy from "~/scene/marker/windy";
import type SceneGeometrySmart from "~/scene/geometry/smart";
import { TActivityType } from "~/interfaces";
import createSDSIcon from "~/ui/icon";
import { activityTypeToMapIcon, pathTypeToMapIcon } from "~/scene/common/icons";
import WasmStoreMarker from "~/wasm/store/wasm-store-marker";
import WasmStoreGeometry from "~/wasm/store/wasm-store-geometry";
import { WASM_ZINDEX } from "~/wasm/const";

const CONFIG_OPTS = {
	SET_ACTIVITY: "setActivity",
	IMPORT_HEIGHT: "importHeight",
};

const GEOM_RED_COLOR = "#f00";
//const GEOM_GRAY_COLOR = "#6b7580";

/**
 * @class GPX
 *
 * gpx-activated
 * gpx-deactivated
 */
export default class GPXItem {
	_opts: {
		parent: GPXContent;
		data: any;
		type: "path" | "point" | "route";
	};

	_data: any;

	_type: "path" | "point" | "route";

	_trackType: TActivityType;

	_origTrackType: TActivityType;

	_config: any;

	_coords: Array<SceneCoords>;

	_geometryCoords: Array<SceneCoords>;

	_markers: Array<SceneMarkerWindy>;

	_geometries: Array<SceneGeometrySmart>;

	_itemData: {
		title: string;
		desc: string;
		svg: string;
		distance: string;
	};

	_activityData: {
		title: string;
		desc: string;
	};

	_geomCardTO: {
		id: any;
		timeout: number;
	};

	_diff: {
		id: number;
		removed: boolean;
		config: any;
		trackType: any;
	};

	_dom: {
		container: HTMLLIElement;
		input: HTMLInputElement;
		inputCard: HTMLInputElement;
		markerEl: HTMLSpanElement;
		cardFrag: DocumentFragment;
		cardEl: HTMLDivElement;
		editBtn?: HTMLButtonElement;
		title?: HTMLSpanElement;
		desc?: HTMLSpanElement;
		deleteBtn?: HTMLButtonElement;
		selector?: HTMLSpanElement;
		descCard?: HTMLParagraphElement;
		titleEl?: HTMLHeadingElement;
		iconHolder?: HTMLSpanElement;
		titleSpanCard?: HTMLSpanElement;
	};

	_wasmMarkers: Array<WasmStoreMarker>;

	_wasmGeometries: Array<WasmStoreGeometry>;

	_id: string;

	constructor(optsArg: any) {
		this._opts = {
			parent: null,
			data: null, // hlavni data
			type: "", // typ
			...optsArg,
		};

		this._data = this._opts.data;
		this._type = this._opts.type;
		this._config = {};
		this._coords = [];
		this._geometryCoords = null;
		this._markers = [];
		this._geometries = [];
		this._itemData = {
			title: "",
			desc: "",
			svg: "",
			distance: "0",
		};
		this._activityData = {
			title: "",
			desc: "",
		};
		this._geomCardTO = {
			id: null,
			timeout: 500,
		};
		this._trackType = "pesky";
		this._origTrackType = "pesky";
		this._diff = {
			id: this.sequenceId,
			removed: false,
			trackType: this._trackType,
			config: this._config,
		};

		this._dom = {
			container: null,
			input: null,
			inputCard: null,
			markerEl: null,
			cardFrag: null,
			cardEl: null,
		};
		this._wasmMarkers = [];
		this._wasmGeometries = [];
		this._id = `wasm-id-${Math.random().toString(16)}`;

		this._routeFix();
		this._processData();
		// vytvorime dom
		this._build();
		// mapa
		this._createMapItems();
		// karta v mape
		this._setCard();
		// nastaveni
		this._setVisual();
	}

	destroy() {
		this._diff.removed = true;

		for (const wasmMarker of this._wasmMarkers) {
			wasmMarker.destroy();
		}

		for (const geometry of this._wasmGeometries) {
			geometry.destroy();
		}

		this._dom.container.remove();
	}

	get container() {
		return this._dom.container;
	}

	get isPoint() {
		return this._type === "point";
	}

	get isPath() {
		return this._type === "path" && !this._config[CONFIG_OPTS.SET_ACTIVITY];
	}

	get isActivity() {
		return this._type === "path" && this._config[CONFIG_OPTS.SET_ACTIVITY];
	}

	get checked() {
		return this._dom.input.checked;
	}

	set checked(state) {
		this._dom.input.checked = state;
		this._dom.inputCard.checked = state;
	}

	set order(value) {
		if (this._dom.markerEl) {
			this._dom.markerEl.textContent = value;
		}
	}

	get coords() {
		return this._coords;
	}

	get geometryCoords() {
		return this._geometryCoords;
	}

	get markers() {
		return this._markers;
	}

	get geometries() {
		return this._geometries;
	}

	get id() {
		return this._id;
	}

	get exportData() {
		const exportData: {
			title: string;
			type: string;
			mark?: any;
			data?: {
				data: any;
				info: any;
			};
		} = {
			title: this._itemData.title,
			type: this._type,
		};

		switch (this._type) {
			case "point":
				// bod
				exportData.mark = this._data.mark;
				break;

			case "path": {
				const data = this._data.data.data.slice();
				const info = { ...this._data.data.info };
				const importHeightOpt = CONFIG_OPTS.IMPORT_HEIGHT in this._config;
				const setActivityOpt = CONFIG_OPTS.SET_ACTIVITY in this._config;

				info.trackType = this._trackType;

				// aktivita
				if (setActivityOpt && this._config[CONFIG_OPTS.SET_ACTIVITY]) {
					exportData.title = this._activityData.title;
					exportData.type = "track";
					// typ aktivity
				}

				if (importHeightOpt && !this._config[CONFIG_OPTS.IMPORT_HEIGHT]) {
					data.forEach(item => {
						if ("altitude" in item) {
							delete item.altitude;
						}
					});
				}

				exportData.data = {
					data,
					info,
				};
				break;
			}

			default:
				//
		}

		return exportData;
	}

	get sequenceId() {
		return this._data.sequenceId || 0;
	}

	get diff() {
		return this._diff;
	}

	set diff(diff) {
		this._config = diff.config;
		// spravna reference
		this._diff.config = this._config;
		this._trackType = diff.trackType;
		// nastavime visual
		this._setVisual(this._config[CONFIG_OPTS.SET_ACTIVITY]);
	}

	set active(state) {
		this._dom.container.classList[state ? "add" : "remove"]("active");
		this._dom.container.title = state && this._dom.title.offsetWidth < this._dom.title.scrollWidth ? this._dom.title.textContent : "";
	}

	setWasmActive(isActive: boolean) {
		for (const wasmMarker of this._wasmMarkers) {
			wasmMarker.update({
				visual: isActive ? "standard" : "inactive",
				bringToTheFront: isActive,
			});
		}

		for (const geometry of this._wasmGeometries) {
			geometry.update({
				visual: isActive ? "standard" : "inactive",
				bringToTheFront: isActive,
			});
		}
	}

	set visibility(visible: boolean) {
		for (const wasmMarker of this._wasmMarkers) {
			wasmMarker.update({
				visible,
			});
		}

		for (const geometry of this._wasmGeometries) {
			geometry.update({
				visible,
			});
		}
	}

	handleEvent(event: Event) {
		switch (event.type) {
			case "click":
				if (this._dom.editBtn && this._dom.editBtn.contains(event.target as Node)) {
					event.preventDefault();
					event.stopPropagation();
					gpxLog("import_edit_item", {
						type: this._getLogType(),
					});
					this._editItem();
				} else if (this._dom.deleteBtn.contains(event.target as Node)) {
					event.preventDefault();
					event.stopPropagation();
					gpxLog("import_delete_item");
					// priznak
					this._diff.removed = true;
					this._opts.parent.remove(this);
					this._opts.parent.diffChange(this);
				} else if (this._dom.selector.contains(event.target as Node)) {
					event.stopPropagation();
					event.preventDefault();
					this._dom.input.checked = !this._dom.input.checked;
					this._dom.inputCard.checked = this._dom.input.checked;
					this._opts.parent.change();
				} else {
					this._click();
				}
				break;

			case "pointerenter":
				{
					this._opts.parent.enter(this);
				}
				break;

			case "pointerleave":
				{
					this._opts.parent.leave(this);
				}
				break;

			default:
				//
		}
	}

	_processData() {
		// geometrie
		if (this.isPoint) {
			this._coords.push(Scene.coordsFromWGS84(this._data.mark.lon, this._data.mark.lat));
		} else {
			let distance = 0;

			this._geometryCoords = [];
			this._data.data.data.forEach(coor => {
				const coords = Scene.stringToCoords(coor.route);

				this._geometryCoords = this._geometryCoords.concat(coords);
				const distances = Mapy.Util.IP.countDistances(coords);

				distance += distances.length ? distances[distances.length - 1] : 0;
			});
			this._itemData.distance = standardDistanceFormat(distance.toString()).string;
			this._coords.push(this._geometryCoords[0]);
			this._coords.push(this._geometryCoords[this._geometryCoords.length - 1]);
		}

		// popisky
		let title = this._data.title || "";
		let desc = this._data.description || "";
		const sequenceId = this.sequenceId;

		switch (this._type) {
			case "point":
				if (!title) {
					title = `${_("gpx.content.point")} ${sequenceId}`;
				}

				if (!desc) {
					desc = `${_("gpx.content.point")} ${Mapy.getComponent("gps").coordsToFormat(this._coords[0])}`;
				}

				// pridame vzdy poradove cislo
				desc = `${sequenceId}. ${desc}`;

				this._itemData.title = title;
				this._itemData.desc = desc;
				this._itemData.svg = "pin";
				break;

			// way vs activity
			case "path":
				{
					const titleNotSet = !title;

					if (titleNotSet) {
						title = `${_("gpx.content.path")} ${sequenceId}`;
					}

					if (!desc) {
						desc = `${_("gpx.content.path")} ${this._itemData.distance}`;
					}

					this._itemData.title = title;
					this._itemData.desc = desc;

					// nastaveni
					if (this._data.possibleEntities.length > 1 && this._data.possibleEntities.indexOf("activity") !== -1) {
						this._config[CONFIG_OPTS.SET_ACTIVITY] = false;
						// data pro aktivitu
						const totalTime = this._data.data.info.totalTime || 0;

						this._activityData.title = titleNotSet ? `${_("gpx.content.activity")} ${sequenceId}` : this._itemData.title;
						this._activityData.desc = `${_("gpx.content.activity")} ${this._itemData.distance} • ${standardTimeFormat(totalTime, true).string}`;
					}

					if (this._data.data.data[0].altitude && this._data.data.data[0].altitude.length) {
						this._config[CONFIG_OPTS.IMPORT_HEIGHT] = true;
					}
				}
				break;

			default:
				//
		}
	}

	_build() {
		// el
		const container = node("li", { className: "item" });
		const selector = node("span", { className: "selector" });

		selector.addEventListener("click", this);

		const itemSelectorRch = Mapy.Util.buildRadioCheck("checkbox", "itemSelector");

		selector.appendChild(itemSelectorRch.label);
		itemSelectorRch.input.checked = true;

		const infoArea = node("span", { className: "info-area" });

		infoArea.addEventListener("pointerenter", this);
		infoArea.addEventListener("pointerleave", this);
		infoArea.addEventListener("click", this);

		const iconHolder = node("span", { className: "icon-holder" });

		infoArea.appendChild(iconHolder);

		const captions = node("span", { className: "captions" });
		const title = node("span", { className: "title" });
		const desc = node("span", { className: "desc" });

		captions.appendChild(title);
		captions.appendChild(desc);
		const controls = node("span", { className: "controls" });
		const deleteBtn = node("button", { className: "btn-delete" });

		deleteBtn.appendChild(icon("delete"));
		deleteBtn.setAttribute("data-log-ignore", "true");
		deleteBtn.addEventListener("click", this);

		// pouze pokud mame config
		if (Object.keys(this._config).length) {
			const editBtn = node("button", { className: "btn-edit" });

			editBtn.appendChild(icon("rename"));
			editBtn.addEventListener("click", this);
			editBtn.setAttribute("data-log-ignore", "true");
			controls.appendChild(editBtn);
			this._dom.editBtn = editBtn;
		}

		controls.appendChild(deleteBtn);
		infoArea.appendChild(captions);
		infoArea.appendChild(controls);
		container.appendChild(selector);
		container.appendChild(infoArea);

		Object.assign(this._dom, {
			container,
			deleteBtn,
			input: itemSelectorRch.input,
			selector,
			iconHolder,
			title,
			desc,
		});
	}

	_setVisual(useActivity = false) {
		const title = useActivity ? this._activityData.title : this._itemData.title;
		const desc = useActivity ? this._activityData.desc : this._itemData.desc;

		this._dom.title.textContent = title;
		this._dom.desc.textContent = desc;
		this._dom.iconHolder.innerHTML = "";
		if (useActivity) {
			this._dom.iconHolder.appendChild(createSDSIcon({ symbol: activityTypeToMapIcon(this._trackType) }));
		} else if (this._type === "path") {
			// @ts-ignore
			this._dom.iconHolder.appendChild(createSDSIcon({ symbol: pathTypeToMapIcon(this._trackType) }));
		} else {
			this._dom.iconHolder.appendChild(icon(this._itemData.svg));
		}

		if (this._dom.titleSpanCard) {
			this._dom.titleEl.title = title;
			this._dom.titleSpanCard.textContent = title;
		}

		if (this._dom.descCard) {
			this._dom.descCard.textContent = desc;
		}

		this._wasmMarkers.forEach(marker => {
			marker.update({
				...marker.data,
				customData: {
					...marker.data.customData,
					title,
					description: desc,
				},
			});
		});
		this._wasmGeometries.forEach(geometry => {
			geometry.update({
				...geometry.data,
				customData: {
					...geometry.data.customData,
					title,
					description: desc,
				},
			});
		});
	}

	_routeFix() {
		if (this._type === "route") {
			if (!Array.isArray(this._data.data.data)) {
				this._data.data.data = [this._data.data.data];
			}

			// zmenime typ na cestu
			this._type = "path";
		}
	}

	_createMapItems() {
		if (this.isPoint) {
			// ulozime pod item
			const index = this.sequenceId;

			this._dom.markerEl = node("span", { className: "text", textContent: index });

			const mainMarker = Scene.createMarkerWindy(this._coords[0], null, {
				type: Scene.VISUAL.MARKER_WINDY.SMALL,
				elm: this._dom.markerEl,
				m3dData: {
					type: "userMarks",
					index,
					enter: () => {
						this._opts.parent.enter(this);
					},
					leave: () => {
						this._opts.parent.leave(this);
					},
				},
			});

			this._markers.push(mainMarker);

			const cont = mainMarker.getContainerSync();

			if (!CONFIG.inFrame && cont) {
				cont.addEventListener("click", () => {
					this._click();
				});
				cont.addEventListener("pointerenter", event => {
					this._setGeomCard(event);
					this._opts.parent.enter(this);
				});
				cont.addEventListener("pointerleave", () => {
					this._geomLeave();
				});
			}

			const wasmMarker = new WasmStoreMarker({
				type: "user-marks",
				lon: this._coords[0].x,
				lat: this._coords[0].y,
				zIndex: WASM_ZINDEX.marker.userMarks,
				interactionType: CONFIG.inFrame ? "none" : "click-hover",
				customData: {
					order: index,
					customId: this._id,
					component: "import",
				},
			});

			this._wasmMarkers.push(wasmMarker);
		} else {
			// vytvorime geometrie
			this._data.data.data.forEach(coor => {
				const coords = Scene.stringToCoords(coor.route);
				// geometrie
				const geometry = Scene.createGeometrySmart(Scene.GEOMETRY.POLYLINE, null, coords, {
					color: GEOM_RED_COLOR,
					width: 4,
					outlineWidth: 0,
				});

				this._geometries.push(geometry);
			});

			const wasmGeometry = new WasmStoreGeometry({
				type: "backpack-route",
				strPoints: this._data.data.data.map(item => item.route),
				zIndex: WASM_ZINDEX.geometry.backpackRoute,
				interactionType: CONFIG.inFrame ? "none" : "click-hover",
				customData: {
					customId: this._id,
					component: "import",
				},
			});

			this._wasmGeometries.push(wasmGeometry);

			// start a cil
			[this._geometryCoords[0], this._geometryCoords[this._geometryCoords.length-1]].forEach(coord => {
				const marker = Scene.createMarkerWindy(coord, null, {
					type: Scene.VISUAL.MARKER_WINDY.LITTLE,
					m3dData: {
						type: "placesRoute",
						enter: () => {
							this._opts.parent.enter(this);
						},
						leave: () => {
							this._opts.parent.leave(this);
						},
					},
				});
				const cont = marker.getContainerSync();

				if (!CONFIG.inFrame && cont) {
					cont.addEventListener("click", () => {
						this._click();
					});
					cont.addEventListener("pointerenter", event => {
						this._setGeomCard(event);
						this._opts.parent.enter(this);
					});
					cont.addEventListener("pointerleave", () => {
						this._geomLeave();
					});
				}
				this._markers.push(marker);

				const wasmMarker = new WasmStoreMarker({
					type: "little",
					lon: coord.x,
					lat: coord.y,
					zIndex: WASM_ZINDEX.marker.userMarks,
					interactionType: CONFIG.inFrame ? "none" : "click-hover",
					customData: {
						customId: this._id,
						component: "import",
					},
				});

				this._wasmMarkers.push(wasmMarker);
			}, this);
			// interaktivita
			this._geometries.forEach(geom => {
				geom.getAllNodes(nodes => {
					nodes.forEach(htmlNode => {
						// spravny kurzor
						htmlNode.style.cursor = "pointer";

						htmlNode.addEventListener("click", () => {
							this._click();
						});
						htmlNode.addEventListener("mouseenter", event => {
							this._setGeomCard(event);
							this._opts.parent.enter(this);
						});
						htmlNode.addEventListener("mouseover", event => {
							this._setGeomCard(event);
						});
						htmlNode.addEventListener("mouseleave", () => {
							this._geomLeave();
						});
					});
				});
			});
		}
	}

	_geomLeave() {
		this._clearGeomCardTO();

		this._geomCardTO.id = setTimeout(() => {
			this._geomCardTO.id = null;
			this._dom.cardEl.remove();
		}, this._geomCardTO.timeout);

		this._opts.parent.leave(this);
	}

	_editItem() {
		const title = this._type === "path" ? _("gpx.content.path.title") : _("gpx.content.route.title");
		const frag = document.createDocumentFragment();
		const importHeightOpt = CONFIG_OPTS.IMPORT_HEIGHT in this._config;
		const setActivityOpt = CONFIG_OPTS.SET_ACTIVITY in this._config;
		let importHeightRB = null;
		let setActivityRB = null;

		// vybiratko typu pro cesty a aktivity
		const activitySelect = new ActivitySelect({
			selected: this._trackType,
			onChange: type => {
				if (type !== "all") {
					this._trackType = type;
					this._diff.trackType = type;
				}
			},
		});

		frag.append(activitySelect.container);

		// je treba delat nadpisy
		if (importHeightOpt && setActivityOpt) {
			frag.appendChild(node("h4", {}, _("gpx.content.import.height")));
		}

		// volba jestli pouzit vysku
		if (importHeightOpt) {
			const heightContainer = node("div", { className: "gpxItem__heightContainer" });
			const itemOpts1Rad = Mapy.Util.buildRadioCheck("radio", "itemOptionsHeight", _("gpx.content.point.option1"));
			const itemOpts2Rad = Mapy.Util.buildRadioCheck("radio", "itemOptionsHeight", _("gpx.content.point.option2"));

			heightContainer.appendChild(itemOpts1Rad.label);
			heightContainer.appendChild(itemOpts2Rad.label);
			frag.appendChild(heightContainer);
			// zaskrtneme
			itemOpts1Rad.input.checked = this._config[CONFIG_OPTS.IMPORT_HEIGHT];
			itemOpts2Rad.input.checked = !this._config[CONFIG_OPTS.IMPORT_HEIGHT];
			importHeightRB = itemOpts1Rad.input;
		}

		// je treba delat nadpisy
		if (importHeightOpt && setActivityOpt) {
			frag.appendChild(node("h4", {}, _("gpx.content.activity.options")));
		}

		// volba mezi cestou a aktivitou
		if (setActivityOpt) {
			const activityOptionsContainer = node("div", { className: "gpxItem__activityOptionsContainer" });
			const option1 = node("div");
			const option2 = node("div");
			const itemOpts1Rad = Mapy.Util.buildRadioCheck("radio", "itemOptionsActivity", _("gpx.content.path.option1"));
			const itemOpts1Info = node("p", { className: "option-info", textContent: _("gpx.content.path.option1Desc") });
			const itemOpts2Rad = Mapy.Util.buildRadioCheck("radio", "itemOptionsActivity", _("gpx.content.path.option2"));
			const itemOpts2Info = node("p", { className: "option-info", textContent: _("gpx.content.path.option2Desc") });

			option1.append(itemOpts1Rad.label, itemOpts1Info);
			option2.append(itemOpts2Rad.label, itemOpts2Info);
			activityOptionsContainer.append(option1, option2);
			frag.appendChild(activityOptionsContainer);
			// zaskrtneme
			itemOpts1Rad.input.checked = !this._config[CONFIG_OPTS.SET_ACTIVITY];
			itemOpts2Rad.input.checked = this._config[CONFIG_OPTS.SET_ACTIVITY];
			setActivityRB = itemOpts1Rad.input;
		}

		const dialog = new Dialog({
			title,
			className: "import-gpx-item-options",
			logSection: "import",
		});
		const buttons = dialog.setSave({
			bindEnter: true,
			cb: () => {
				let wasChange = false;

				if (importHeightOpt) {
					const heightChange = this._config[CONFIG_OPTS.IMPORT_HEIGHT] !== importHeightRB.checked;

					if (heightChange) {
						gpxLog("import_edited_item", {
							type: this._getLogType(),
							text: this._config[CONFIG_OPTS.IMPORT_HEIGHT] ? "Výška model" : "Výška import",
						});
					}

					this._config[CONFIG_OPTS.IMPORT_HEIGHT] = importHeightRB.checked;
					wasChange = true;
				}

				if (setActivityOpt) {
					const activityChange = this._config[CONFIG_OPTS.SET_ACTIVITY] === setActivityRB.checked;

					if (activityChange) {
						gpxLog("import_edited_item", {
							type: this._getLogType(),
							text: this._config[CONFIG_OPTS.SET_ACTIVITY] ? "Uložit jako cestu" : "Uložit jako aktivitu",
						});
					}

					this._config[CONFIG_OPTS.SET_ACTIVITY] = !setActivityRB.checked;
					this._setVisual(!setActivityRB.checked);
					this._opts.parent.changeType();
					wasChange = true;
				} else {
					this._setVisual();
				}

				if (this._trackType !== this._origTrackType) {
					wasChange = true;
				}

				// zmena -> propagace do contentu
				if (wasChange) {
					this._opts.parent.diffChange(this);
				}
			},
		});

		buttons.saveBtn.setAttribute("data-log-ignore", "true");
		dialog.open();
		dialog.dom.content.appendChild(frag);
	}

	_click() {
		this._opts.parent.click(this);
	}

	_setCard() {
		// obsah
		const infoPart = node("span", { className: "info-part" });
		const title = node("h5");
		const titleSpan = node("span");
		const desc = node("p", { className: "address" });
		const sepPart = node("span", { className: "separator-part" });
		const innerSepPart = node("span", { className: "separator" });
		const buttonPart = node("span", { className: "button-part" });
		const checkbox = Mapy.Util.buildRadioCheck("checkbox", "selectebox");

		title.appendChild(titleSpan);
		infoPart.appendChild(title);
		infoPart.appendChild(desc);
		infoPart.addEventListener("click", () => {
			this._click();
		});
		sepPart.appendChild(innerSepPart);
		buttonPart.addEventListener("click", event => {
			event.stopPropagation();
			event.preventDefault();
			checkbox.input.checked = !checkbox.input.checked;
			this._dom.input.checked = checkbox.input.checked;
			this._opts.parent.change();
		});

		buttonPart.appendChild(checkbox.label);
		checkbox.input.checked = this._dom.input.checked;
		this._dom.cardFrag = document.createDocumentFragment();
		this._dom.cardFrag.appendChild(infoPart);
		this._dom.cardFrag.appendChild(sepPart);
		this._dom.cardFrag.appendChild(buttonPart);
		this._dom.inputCard = checkbox.input;
		this._dom.titleEl = title;
		this._dom.titleSpanCard = titleSpan;
		this._dom.descCard = desc;

		// vizitka
		this._dom.cardEl = node("div", { className: "card marker-popup geom-popup" });

		const cardHeader = node("div", { className: "card-header" });
		const cardBody = node("div", { className: "card-body" });
		const cardFooter = node("div", { className: "card-footer" });
		const tail = node("div", { className: "tail" });

		this._dom.cardEl.appendChild(cardHeader);
		this._dom.cardEl.appendChild(cardBody);
		this._dom.cardEl.appendChild(cardFooter);
		this._dom.cardEl.appendChild(tail);
		cardBody.appendChild(this._dom.cardFrag);

		this._dom.cardEl.addEventListener("mouseenter", event => {
			event.stopPropagation();
			event.preventDefault();
			this._clearGeomCardTO();
			this._opts.parent.enter(this);
		});

		this._dom.cardEl.addEventListener("mouseleave", event => {
			event.stopPropagation();
			event.preventDefault();
			this._clearGeomCardTO();
			this._dom.cardEl.remove();
			this._opts.parent.leave(this);
		});
	}

	_setGeomCard(event) {
		this._clearGeomCardTO();
		document.querySelector("#scene #map div").appendChild(this._dom.cardEl);
		this._dom.cardEl.style.left = `${event.clientX}px`;
		this._dom.cardEl.style.top = `${event.clientY}px`;
	}

	_clearGeomCardTO() {
		if (this._geomCardTO.id) {
			clearTimeout(this._geomCardTO.id);
			this._geomCardTO.id = null;
		}
	}

	_getLogType() {
		return this._config[CONFIG_OPTS.SET_ACTIVITY] ? "activity" : this._type;
	}
}
